import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { statusColor } from '@platform/core/lib/statusColors';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useCreateRecordMutation, useMeQuery } from '@platform/state/api';
import { ErrorBanner } from '@platform/ui';
import { useNextCode } from '../form/create/useNextCode';
import { normalizeValue } from '../form/draftChanges';
import { useTitlePlaceholder } from '../form/header/useTitlePlaceholder';
import type { INewChildRowProps } from './INewChildRowProps';

/**
 * Новий дочірній запис на місці (прототип v4 chNew: «+ Нове дочірнє завдання» одразу додає рядок у список, без діалогу й переходу): рядок
 * зверху списку з наступним номером типу, полем назви у фокусі («Завдання без назви»), початковим статусом і виконавцем «я»; Enter чи вихід із поля створює запис
 * з батьком, напрямом та ітерацією батьківського запису, Esc або × — відкидає; порожня назва — нічого не створює.
 */
export function NewChildRow({ ws, rt, parent, parentKey, onDone }: INewChildRowProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const { data: me } = useMeQuery();
  const placeholder = useTitlePlaceholder(rt);
  const next = useNextCode(ws, rt.key);
  const [title, setTitle] = useState('');
  const [create, { isLoading, error }] = useCreateRecordMutation();
  const initial = rt.statuses.initial ?? rt.statuses.list[0]?.value ?? '';
  const hasAssignee = rt.fields.some((f) => f.key === 'assignee' && f.type === 'user');
  const submit = () => {
    const text = title.trim();
    if (!text) { onDone(); return; }
    if (isLoading) return;
    const values: Record<string, unknown> = { title: text, [parentKey]: parent.id };
    for (const k of ['area', 'iteration']) if (parent.values[k] != null) values[k] = normalizeValue(parent.values[k]);
    if (me && hasAssignee) values['assignee'] = me.id;
    void create({ ws, rt: rt.key, body: { values } }).unwrap().then((rec) => onDone(rec)).catch(() => undefined);
  };
  return (
    <>
      <div className="rtab-child rtab-child--new">
        <span className="rtab-child__code" title={t('recordPage.draft.codeTitle')}>{next ?? '…'}</span>
        <input className="control rtab-child__input" autoFocus value={title} placeholder={placeholder} aria-label={t('recordPage.titleLabel')} disabled={isLoading}
          onChange={(e) => setTitle(e.target.value)} onBlur={submit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } else if (e.key === 'Escape') onDone(); }} />
        <span className="rtab-child__status"><span className="dot" style={{ background: statusColor(rt.statuses, initial, rt.key) }} />{strings.status(rt.key, initial, initial)}</span>
        <span className="rtab-child__who">{hasAssignee ? me?.displayName ?? '—' : '—'}</span>
        <span className="rtab-child__due" />
        <span className="rtab-child__rem" />
        <button type="button" className="rtab-x" title={t('recordPage.children.discardNew')} aria-label={t('recordPage.children.discardNew')}
          onMouseDown={(e) => e.preventDefault()} onClick={() => onDone()}>×</button>
      </div>
      <ErrorBanner error={error} />
    </>
  );
}
